import React, { useRef, useState, useEffect } from "react";
import { Power, Camera as CamIcon, Aperture, Zap, Download, Maximize2, Timer } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Panel, Overline } from "../common/Panel";
import { toast } from "sonner";

const FALLBACK = "https://images.unsplash.com/photo-1702549956329-0888cfa45092?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

function Btn({ children, onClick, testid, variant = "default", active }) {
  const base = "inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium border transition-colors active:scale-[0.98]";
  const v = variant === "danger" ? "border-[var(--danger)]/40 text-[var(--danger)] hover:bg-[var(--danger)]/10"
    : active ? "bg-[var(--accent)] text-black border-[var(--accent)]"
    : "bg-[var(--surface2)] border-[var(--bd)] hover:border-[var(--bd-strong)]";
  return <button data-testid={testid} onClick={onClick} className={`${base} ${v}`}>{children}</button>;
}

function Toggle({ on, onClick, testid }) {
  return <button data-testid={testid} onClick={onClick} className={`w-11 h-6 rounded-full relative transition-colors ${on ? "bg-[var(--accent)]" : "bg-[var(--track)]"}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-0.5"}`} /></button>;
}

export default function CameraTab() {
  const { settings, updateSettings, addSnapshot } = useApp();
  const [online, setOnline] = useState(false);
  const [lastSnap, setLastSnap] = useState(null);
  const [rapid, setRapid] = useState(false);
  const [flash, setFlash] = useState(false);
  const [autoSnap, setAutoSnap] = useState(false);
  const [autoInterval, setAutoInterval] = useState(10);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setOnline(true); toast.success("Camera online");
    } catch { setOnline(true); toast.info("No webcam — using simulated feed"); }
  };
  const stop = () => { if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; setOnline(false); toast.info("Camera offline"); };

  const snap = () => {
    if (settings.camAutoFlash && online) toast.info("⚡ Flash fired");
    let dataUrl = FALLBACK;
    const v = videoRef.current;
    if (online && v && v.videoWidth) {
      const c = document.createElement("canvas");
      c.width = v.videoWidth; c.height = v.videoHeight;
      c.getContext("2d").drawImage(v, 0, 0);
      dataUrl = c.toDataURL("image/jpeg", 0.8);
    }
    setLastSnap(dataUrl); addSnapshot(dataUrl);
  };

  useEffect(() => {
    if (!autoSnap || !online) return;
    const id = setInterval(snap, autoInterval * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [autoSnap, autoInterval, online]);

  useEffect(() => () => { if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Overline>TTGO T-Camera</Overline>
          <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">Camera Management</h2>
        </div>
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-mono" style={{ color: online ? "var(--accent)" : "var(--t2)", borderColor: online ? "var(--accent)" : "var(--bd)" }} data-testid="camera-status">
          <span className="w-2 h-2 rounded-full" style={{ background: online ? "var(--accent)" : "var(--t2)" }} />{online ? "ONLINE" : "OFFLINE"}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel className="p-6 space-y-5">
          <div className="flex flex-wrap gap-2">
            <Btn testid="cam-on" onClick={start} active={online}><Power size={15} /> Camera ON</Btn>
            <Btn testid="cam-off" onClick={stop} variant="danger"><Power size={15} /> Camera OFF</Btn>
            <Btn testid="cam-snap" onClick={snap}><Aperture size={15} /> Take Snapshot</Btn>
          </div>

          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Timer size={15} className="text-[var(--accent)]" /><span className="text-sm">Rapid snapshot mode</span></div>
              <Toggle on={rapid} testid="cam-rapid-toggle" onClick={() => setRapid((r) => !r)} />
            </div>
            {rapid && (
              <select data-testid="cam-rapid-interval" className="w-full bg-[var(--surface2)] border border-[var(--bd)] rounded-md px-3 py-2 text-sm font-mono" value={settings.rapidInterval} onChange={(e) => updateSettings({ rapidInterval: parseInt(e.target.value) })}>
                {[3, 5, 10, 30, 60].map((v) => <option key={v} value={v}>{v < 60 ? `${v}s` : "1 min"}</option>)}
              </select>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Zap size={15} className="text-[var(--accent)]" /><span className="text-sm">LED Flash</span></div>
              <Toggle on={flash} testid="cam-flash-toggle" onClick={() => { setFlash((f) => !f); toast.info(`Flash ${!flash ? "ON" : "OFF"}`); }} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm pl-7">Auto-flash on snapshot</span>
              <Toggle on={settings.camAutoFlash} testid="cam-autoflash-toggle" onClick={() => updateSettings({ camAutoFlash: !settings.camAutoFlash })} />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><CamIcon size={15} className="text-[var(--accent)]" /><span className="text-sm">Auto snapshot</span></div>
              <Toggle on={autoSnap} testid="cam-auto-toggle" onClick={() => setAutoSnap((a) => !a)} />
            </div>
            {autoSnap && (
              <select data-testid="cam-auto-interval" className="w-full bg-[var(--surface2)] border border-[var(--bd)] rounded-md px-3 py-2 text-sm font-mono" value={autoInterval} onChange={(e) => setAutoInterval(parseInt(e.target.value))}>
                {[3, 5, 10, 30, 60].map((v) => <option key={v} value={v}>{v < 60 ? `${v}s` : "1 min"}</option>)}
              </select>
            )}
          </div>
        </Panel>

        <Panel className="p-6">
          <Overline>Last Snapshot</Overline>
          <video ref={videoRef} autoPlay playsInline muted className="hidden" />
          <div className="mt-3 aspect-video rounded-md bg-[var(--surface2)] border border-[var(--bd)] overflow-hidden flex items-center justify-center" data-testid="snapshot-viewer">
            {lastSnap ? (
              <img src={lastSnap} alt="snapshot" className="w-full h-full object-cover" data-testid="snapshot-image" />
            ) : (
              <div className="text-center text-[var(--t2)] p-8">
                <CamIcon size={40} className="mx-auto mb-3 opacity-40" />
                <p className="text-sm font-mono">No image received yet</p>
                <p className="text-xs mt-1">Take a snapshot to capture from the camera feed</p>
              </div>
            )}
          </div>
          {lastSnap && (
            <div className="flex gap-2 mt-3">
              <a href={lastSnap} download={`snapshot-${Date.now()}.jpg`} data-testid="snapshot-download" className="flex-1 py-2 rounded-md bg-[var(--surface2)] border border-[var(--bd)] text-sm flex items-center justify-center gap-2 hover:border-[var(--bd-strong)] transition-colors"><Download size={15} /> Download</a>
              <button onClick={() => window.open(lastSnap, "_blank")} data-testid="snapshot-fullscreen" className="flex-1 py-2 rounded-md bg-[var(--surface2)] border border-[var(--bd)] text-sm flex items-center justify-center gap-2 hover:border-[var(--bd-strong)] transition-colors"><Maximize2 size={15} /> Fullscreen</button>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
